import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadGoogleCharts } from './googleChartsLoader'; // Adjust path as needed
import ApiURl from '../Paths/Api'; // Adjust path as needed

const Supervisor = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState({
    attendee_status: [['Status', 'Percentage'], ['Attended', 0], ['Not Attended', 0]],
    meeting_status: [['Status', 'Percentage'], ['Active', 0], ['Completed', 0], ['Cancelled', 0], ['Other', 0]],
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch chart data from API
  const fetchChartData = async () => {
    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        throw new Error("Phone number not found in local storage");
      }

      console.log("[fetchChartData] Sending request to get_meetings.php", { phone });
      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("[fetchChartData] API Response:", data);

      if (data.success && data.charts) {
        setChartData({
          attendee_status: [
            ['Status', 'Percentage'],
            ['Attended', data.charts.attendee_status.attended],
            ['Not Attended', data.charts.attendee_status.not_attended],
          ],
          meeting_status: [
            ['Status', 'Percentage'],
            ['Active', data.charts.meeting_status.active],
            ['Completed', data.charts.meeting_status.completed],
            ['Cancelled', data.charts.meeting_status.cancelled],
            ['Other', data.charts.meeting_status.other],
          ],
        });
      } else {
        console.error("[fetchChartData] No chart data found:", data.message || "Empty data");
        setChartData({
          attendee_status: [['Status', 'Percentage'], ['Attended', 0], ['Not Attended', 0]],
          meeting_status: [['Status', 'Percentage'], ['Active', 0], ['Completed', 0], ['Cancelled', 0], ['Other', 0]],
        });
      }
    } catch (err) {
      console.error("[fetchChartData] Error:", err.message, { stack: err.stack });
      setChartData({
        attendee_status: [['Status', 'Percentage'], ['Attended', 0], ['Not Attended', 0]],
        meeting_status: [['Status', 'Percentage'], ['Active', 0], ['Completed', 0], ['Cancelled', 0], ['Other', 0]],
      });
    }
  };

  // Initialize Google Charts
  useEffect(() => {
    let isMounted = true;

    fetchChartData().then(() => {
      loadGoogleCharts().then(() => {
        if (!isMounted) return;

        // Attendee Status Pie Chart
        const attendeeDataTable = window.google.visualization.arrayToDataTable(chartData.attendee_status);
        const attendeeOptions = {
          title: 'Attendance Status',
          titleTextStyle: {
            color: '#1F2937',
            fontName: 'Inter, sans-serif',
            fontSize: 20,
            bold: true,
          },
          colors: ['#14B8A6', '#F87171'],
          backgroundColor: '#F3F4F6',
          chartArea: { width: '80%', height: '70%' },
          legend: {
            position: 'bottom',
            textStyle: {
              color: '#1F2937',
              fontName: 'Inter, sans-serif',
              fontSize: 14,
            },
          },
          pieSliceBorderColor: '#F3F4F6',
          pieSliceTextStyle: {
            color: '#FFFFFF',
            fontName: 'Inter, sans-serif',
            fontSize: 14,
          },
          is3D: true,
          animation: {
            startup: true,
            duration: 1000,
            easing: 'out',
          },
        };

        const attendeeChart = new window.google.visualization.PieChart(
          document.getElementById('attendanceChart')
        );
        attendeeChart.draw(attendeeDataTable, attendeeOptions);

        // Meeting Status Pie Chart
        const meetingDataTable = window.google.visualization.arrayToDataTable(chartData.meeting_status);
        const meetingOptions = {
          title: 'Meeting Status',
          titleTextStyle: {
            color: '#1F2937',
            fontName: 'Inter, sans-serif',
            fontSize: 20,
            bold: true,
          },
          colors: ['#06B6D4', '#10B981', '#EF4444', '#6B7280'],
          backgroundColor: '#F3F4F6',
          chartArea: { width: '80%', height: '70%' },
          legend: {
            position: 'bottom',
            textStyle: {
              color: '#1F2937',
              fontName: 'Inter, sans-serif',
              fontSize: 14,
            },
          },
          pieSliceBorderColor: '#F3F4F6',
          pieSliceTextStyle: {
            color: '#FFFFFF',
            fontName: 'Inter, sans-serif',
            fontSize: 14,
          },
          is3D: true,
          animation: {
            startup: true,
            duration: 1000,
            easing: 'out',
          },
        };

        const meetingChart = new window.google.visualization.PieChart(
          document.getElementById('meetingChart')
        );
        meetingChart.draw(meetingDataTable, meetingOptions);
      }).catch((error) => {
        console.error('Failed to load Google Charts:', error);
      });
    });

    return () => {
      isMounted = false;
    };
  }, [chartData]);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans flex flex-col">
      {/* Embedded CSS for animations and responsiveness */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-grow {
          animation: grow 0.6s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes grow {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .chart-container {
          position: relative;
          width: 100%;
          height: 350px; /* Mobile */
        }
        @media (min-width: 768px) {
          .chart-container {
            height: 400px; /* Desktop */
          }
          .sidebar {
            position: fixed;
            transform: translateX(0) !important; /* Ensure always open */
          }
        }
        .sidebar {
          transition: transform 0.3s ease-in-out;
        }
        .sidebar-open {
          transform: translateX(0);
        }
        .sidebar-closed {
          transform: translateX(-100%);
        }
      `}</style>

      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-3 sm:p-4 shadow-lg fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            {/* Hamburger Menu for Mobile */}
            <button
              className="sm:hidden mr-3 focus:outline-none"
              onClick={toggleSidebar}
              aria-label="Toggle Sidebar"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="text-lg sm:text-xl font-bold tracking-tight">Welcome, Supervisor</div>
          </div>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-teal-600 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-teal-700 transition-all duration-300 transform hover:scale-105"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="flex flex-1 pt-16 sm:pt-20">
        {/* Sidebar */}
        <div
          className={`w-60 sm:w-64 bg-teal-600 text-white p-4 sm:p-6 flex flex-col fixed h-full shadow-lg z-30 md:w-72 md:static md:translate-x-0 sidebar ${
            isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'
          } md:sidebar-open`}
        >
          <div className="flex justify-between items-center mb-4 sm:hidden">
            <span className="text-base font-semibold">Menu</span>
            <button
              onClick={toggleSidebar}
              className="text-white focus:outline-none"
              aria-label="Close Sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <Link
            to="/reports"
            className="py-2 px-3 text-left text-sm sm:text-base font-medium text-white hover:bg-teal-700 transition-all duration-300 rounded-lg mb-3 transform hover:scale-105 animate-slide-in"
            onClick={() => setIsSidebarOpen(false)}
          >
            Reports
          </Link>
          <Link
            to="/scann"
            className="py-2 px-3 text-left text-sm sm:text-base font-medium text-white hover:bg-teal-700 transition-all duration-300 rounded-lg transform hover:scale-105 animate-slide-in"
            onClick={() => setIsSidebarOpen(false)}
          >
            Scan
          </Link>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 md:ml-72">
          <div className="max-w-5xl mx-auto md:max-w-7xl">
            <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 animate-fade-in">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-teal-600 mb-3 sm:mb-4 md:mb-6 text-center">
                Supervisor Dashboard
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8 text-center">
                Welcome to the Supervisor Dashboard. Explore event insights below or select a page from the sidebar.
              </p>

              {/* Pie Charts */}
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-6">
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6 shadow-sm animate-grow">
                  <div className="chart-container">
                    <div id="attendanceChart"></div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6 shadow-sm animate-grow">
                  <div className="chart-container">
                    <div id="meetingChart"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative w-full py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm z-10">
        <a href="https://algo2botsinfotech.com/home/" className="hover:text-teal-600 transition-all duration-300">
          Powered by @ Algo2bots Infotech • {new Date().getFullYear()}
        </a>
      </footer>
    </div>
  );
};

export default Supervisor;